// FirestoreUtils.js
import { getFirestore, doc, setDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { app } from '../../firebaseConfig';

const db = getFirestore(app);

export const getAllOrdersFromFirestore = async () => {
  const allOrders = [];

  try {
    // Step 1: Get all user documents (in enviaorders)
    const userDocsSnapshot = await getDocs(collection(db, 'enviaorders'));

    if (userDocsSnapshot.empty) {
      console.warn('⚠️ No documents found in enviaorders.');
    }

    // Step 2: For each user document
    const fetchUserOrders = userDocsSnapshot.docs.map(async (userDoc) => {
      const userEmail = userDoc.id;
      const userDocData = userDoc.data();

      const userOrdersRef = collection(db, 'enviaorders', userEmail, 'userOrders');
      const ordersSnapshot = await getDocs(userOrdersRef);

      return ordersSnapshot.docs.map((orderDoc) => {
        const orderData = orderDoc.data();

        return {
          userEmail,
          trackingNumber: orderDoc.id,
          ...orderData,
        };
      });
    });

    // Step 3: Await all users' order collections
    const ordersByUser = await Promise.all(fetchUserOrders);

    // Step 4: Flatten the nested results into one big list
    ordersByUser.forEach((orders) => allOrders.push(...orders));

    return allOrders;
  } catch (error) {
    console.error('❌ Error retrieving all orders from Firestore:', error);
    throw error;
  }
};
export const saveOrderToFirestore = async (userEmail, orderData) => {
  try {
    // Validate userEmail
    if (typeof userEmail !== 'string' || !userEmail) {
      throw new Error('Invalid userEmail: must be a non-empty string');
    }

    // Validate shipments array
    if (!Array.isArray(orderData.shipments) || orderData.shipments.length === 0) {
      throw new Error('No shipments found in orderData');
    }

    // Clean the payload to remove undefined values
    const cleanedPayload = JSON.parse(
      JSON.stringify(orderData.payload, (key, value) => (value === undefined ? null : value))
    );

    // Iterate over each shipment and save as a separate document
    for (const shipment of orderData.shipments) {
      const trackingNumber = String(shipment.trackingNumber);
      if (!trackingNumber) {
        throw new Error('Invalid trackingNumber: must be provided for each shipment');
      }

      // Data for each shipment document
      const shipmentData = {
        shipmentId: shipment.shipmentId,
        trackUrl: shipment.trackUrl,
        trackingNumber: shipment.trackingNumber,
        carrier: shipment.carrier,
        label: shipment.label,
        service: shipment.service,
        declaredValue: shipment.declaredValue,
        shippingCost: shipment.shippingCost,
        totalPrice: shipment.totalPrice,
        createdAt: shipment.createdAt || new Date().toISOString(),
        content: shipment.content,
      };

      // Save each shipment as a separate document with trackingNumber as the key
      await setDoc(
        doc(db, 'enviaorders', userEmail, 'userOrders', trackingNumber),
        shipmentData,
        { merge: true }
      );
    }
  } catch (error) {
    console.error('❌ Error saving order to Firestore:', error);
    throw error;
  }
};

export const getAllTracking = async (userEmail) => {
  try {
    const ordersRef = collection(db, 'enviaorders', userEmail, 'userOrders');
    const querySnapshot = await getDocs(ordersRef);
    const trackingNumbers = querySnapshot.docs.map((doc) => ({
      trackingNumber: doc.id,
      carrier: doc.data().carrier,
      label: doc.data().label,
      service: doc.data().service,
      shipmentId: doc.data().shipmentId,
      declaredValue: doc.data().declaredValue,
      shippingCost: doc.data().shippingCost,
      totalPrice: doc.data().totalPrice,
      trackUrl: doc.data().trackUrl,
      createdAt: doc.data().createdAt,
      content: doc.data().content,
    }));
    return trackingNumbers;
  } catch (error) {
    console.error('❌ Error fetching tracking numbers:', error);
    return [];
  }
};

// Function to upload tracking data to Firestore
export const upload_tracking_data_to_cloud = async (
  orderId,
  userEmail,
  trackingNumber,
  trackUrl,
  shipmentId,
  carrier
) => {
  try {
    // Validate inputs
    if (!userEmail) {
      throw new Error('User email is required');
    }
    if (!orderId) {
      throw new Error('Order ID is required');
    }
    if (!trackingNumber) {
      throw new Error('Tracking number is required');
    }

    // Sanitize inputs
    const sanitizedUserEmail = String(userEmail).trim().toLowerCase();
    const sanitizedOrderId = String(orderId).trim();
    const sanitizedTrackingNumber = String(trackingNumber).trim();
    const sanitizedTrackUrl = trackUrl !== undefined ? String(trackUrl).trim() : null;
    const sanitizedShipmentId = shipmentId !== undefined ? String(shipmentId).trim() : '';
    const sanitizedCarrier = carrier !== undefined ? String(carrier).trim() : '';

    // Reference to the tracking document within the nested structure:
    // tracking > userEmail > orders > orderId > trackings > trackingNumber
    const trackingDocRef = doc(
      db,
      'tracking',
      sanitizedUserEmail,
      'orders',
      sanitizedOrderId,
      'trackings',
      sanitizedTrackingNumber
    );

    // Data to store in the document
    const trackingData = {
      trackingNumber: sanitizedTrackingNumber,
      trackUrl: sanitizedTrackUrl,
      shipmentId: sanitizedShipmentId,
      carrier: sanitizedCarrier,
      createdAt: new Date().toISOString(),
    };

    // Set the document with the tracking data (creates collections/documents if they don't exist)
    await setDoc(trackingDocRef, trackingData);
    return { success: true, message: 'Tracking data uploaded successfully' };
  } catch (error) {
    console.error('Error uploading tracking data to Firestore:', error);
    return { success: false, error: error.message };
  }
};

// Function to fetch all tracking data for a given user email and order ID from Firestore
export const get_tracking_data_from_cloud = async (userEmail, orderId) => {
  try {
    // Validate inputs
    if (!userEmail) {
      throw new Error('User email is required');
    }
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    // Sanitize inputs (same as upload_tracking_data_to_cloud)
    const sanitizedUserEmail = String(userEmail).trim().toLowerCase();
    const sanitizedOrderId = String(orderId).trim();

    // Reference to the trackings subcollection
    const trackingsCollectionRef = collection(
      db,
      'tracking',
      sanitizedUserEmail,
      'orders',
      sanitizedOrderId,
      'trackings'
    );

    // Fetch all documents in the trackings subcollection
    const querySnapshot = await getDocs(trackingsCollectionRef);

    if (querySnapshot.empty) {
      return { success: false, error: 'No tracking data found' };
    }

    // Map the documents to an array of tracking data
    const trackingData = querySnapshot.docs.map((doc) => ({
      trackingNumber: doc.data().trackingNumber,
      trackUrl: doc.data().trackUrl,
      shipmentId: doc.data().shipmentId,
      carrier: doc.data().carrier,
      createdAt: doc.data().createdAt,
    }));

    return {
      success: true,
      data: trackingData,
    };
  } catch (error) {
    console.error('Error fetching tracking data from Firestore:', error);
    return { success: false, error: error.message };
  }
};